import {
  BoldOutlined,
  CloseOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  FileImageOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  SmileOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Alert, Button, Chip, Input, Spinner, Tooltip } from '@mm/components';
import { useUploadActions } from '@mm/state/actions/uploadActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { AIEmailTemplate } from '@mm/state/interfaces/aiEmailTemplates';
import WidgetModel, {
  EmailConfig,
  WidgetBase,
} from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { colors, getUniqId } from '@mm/utils';
import { ReactComponent as MagicPencilIcon } from 'assets/icons/magic-pen.svg';
import classNames from 'classnames';
import { cloneDeep, isEqual, set } from 'lodash';
import { EditorView } from 'prosemirror-view';
import React, { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { ProseMirror } from 'use-prosemirror';
import { ErrorIcon } from '../../../../../../common/component-icons/ErrorIcon/ErrorIcon';
import AttributePicker from '../../../../../../common/components/AttributePicker/AttributePicker';
import { EmojiPicker } from '../../../../../../common/components/EmojiPicker/EmojiPicker';
import { getFormatFromType } from '../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import {
  extractEmailsFromString,
  useEmailWidgetHandlers,
} from '../../../../../../common/helpers/widget-helpers/use-email-widget-handlers';
import AttributeContainingTextInput from '../../../../AttributeContainingTextInput/AttributeContainingTextInput';
import { LinkPrompt } from '../editor/components/Link/LinkPrompt';
import { useEditorImageHandlers } from '../editor/hooks/use-editor-image-handlers';
import { ReactComponent as BracketsOutlined } from '../svg/brackets.svg';
import { useEditor } from '../use-editor-old';
import { useAiEmailTemplate } from './use-ai-email-template';
import { useMasterOrEntityEmailFormHandlers } from './use-master-or-entity-email-form-handlers';

import { Dropdown, Space } from 'antd';
import { HtmlEditor } from '../HtmlEditor/HtmlEditor';
import css from '../SlideoutEmail.module.css';
import cssForm from './EmailForm.module.css';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

const MAX_ATTACHMENTS = 8;
const CC_CAP = 5;
const BCC_CAP = 5;
const MAX_FILE_SIZE_IN_MB = 8;
const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_MB * 1024 * 1024;

const ACCEPTED_FILE_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.jpg',
  '.jpeg',
  '.png',
  '.bmp',
  '.gif',
  '.tif',
  '.tiff',
  '.psd',
  '.al',
  '.txt',
  '.rtf',
  '.mobi',
  '.epub',
];

// tslint:disable-next-line: cyclomatic-complexity
export const EmailForm = ({
  stateAndHandlers,
  widget,
  entityUrl,
  onUpgradeClick,
  styles,
  onSignatureClick,
}: {
  stateAndHandlers: ReturnType<typeof useEmailWidgetHandlers>;
  widget: WidgetModel;
  entityUrl?: string;
  onUpgradeClick: () => void;
  styles?: {
    container?: string;
    optionalBlock?: string;
  };
  onSignatureClick?: () => void;
}) => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const editorRef = useRef<{ view: EditorView }>(null);
  const {
    configEntities,
    setConfigEntities,
    // Note: at least in the first iteration these properties can only be present on the master, not entities
    ddEditorValue,
    setDdEditorValue,
  } = stateAndHandlers;

  const {
    config: { entities, emailSendsCount },
  } = widget as unknown as WidgetBase<EmailConfig>;

  const onAiContainerClick = () => {
    aiCancel();
  };

  const ai = useSelector((state: StoreState) => state.aiEmailTemplates);

  const { body, subject, previewText } =
    entities?.find(entity => entity.url === entityUrl) || {};

  const currentEntity = configEntities.find(ent => ent.url === entityUrl);
  const currentEntityIndex = configEntities.findIndex(
    ent => ent.url === entityUrl,
  );

  const isMaster = currentEntity?.url === 'master' || !currentEntity;

  const {
    subjectValue,
    setSubjectValue,
    previewTextValue,
    bodyValue,
    setBodyValue,
    onChangeSubject,
    onChangePreviewText,
    setEmailSubjectRef,
    setEmailPreviewTextRef,
    updateInsertEmojiSubject,
    updateInsertEmojiPreviewText,
    updateInsertAttributeSubject,
    updateInsertAttributePreviewText,
    ccInputValue,
    setCcInputValue,
    setBccInputValue,
    bccInputValue,
    ccEmails,
    setCCEmails,
    bccEmails,
    setBCCEmails,
    attachments,
    setAttachments,
  } = useMasterOrEntityEmailFormHandlers({
    widget,
    isMaster,
    currentEntity,
    masterStateAndHandlers: stateAndHandlers,
  });

  const editor = useEditor(bodyValue);

  const getEntitiesConfig = (path, value) => {
    const updatedConfig = cloneDeep(configEntities);
    if (currentEntity) {
      set(updatedConfig[currentEntityIndex], path, value);
    } else {
      set(updatedConfig, path, value);
    }

    return updatedConfig;
  };

  const updateConfig = (path, value) => {
    const updatedConfig = getEntitiesConfig(path, value);
    setConfigEntities(updatedConfig);
  };

  const onAiEmailTemplateLoad = (aiEmailTemplate: AIEmailTemplate) => {
    if (subjectValue || bodyValue) return; // do not override user input

    const { subject, body } = aiEmailTemplate.emailTemplate || {};

    if (subject) {
      updateConfig('subject', subject);
      setSubjectValue(subject);
    }

    if (body) editor.insertParagraphs(body, false);
  };

  const { cancel: aiCancel } = useAiEmailTemplate({
    onAiEmailTemplateLoad,
    widget: widget as unknown as WidgetBase<EmailConfig>,
  });

  const [selectedEditorType, setSelectedEditorType] = useState(
    ddEditorValue ? 1 : 0,
  );

  const editorDropdownItems = [
    {
      label: 'Simple text',
      key: 0,
    },
    {
      label: 'Drag & drop editor',
      key: 1,
    },
  ];

  const editorDropdownMenuProps = {
    items: editorDropdownItems,
    style: { zIndex: 1500002 },
    onClick: ({ key }: { key: string }) => {
      setSelectedEditorType(Number(key));
      setDdEditorValue(Number(key) === 1);
    },
  };

  const {
    handleDrop,
    handlePaste,
    onImageSelected,
    imagesScaledDown,
    maxImageWidth,
    maxImageHeight,
  } = useEditorImageHandlers(editorRef.current?.view);

  const imageUploadSelectorRef = useRef<HTMLInputElement>(null);
  const cache = useRef({
    subject,
    body,
    previewText,
  });
  const modalActions = useModalActions();
  const viewActions = useViewActions();
  const uploadActions = useUploadActions();

  const insertBodyAttribute = (value: string) => {
    editor.insertText(`{{${value}}}`, undefined);
    editorRef.current?.view?.focus?.();
  };

  const [userWritesManually, setUserWritesManually] = useState<null | boolean>(
    null,
  );

  const emailInputPlaceholder = 'Enter email here and press enter to save';
  const maxReachText = `You have reached the limit of {count} emails`;
  const ccMaxReachText = maxReachText.replace('{count}', String(CC_CAP));
  const bccMaxReachText = maxReachText.replace('{count}', String(BCC_CAP));
  const ccDisabled = ccEmails.length >= CC_CAP;
  const bccDisabled = bccEmails.length >= BCC_CAP;
  const activeAttachments = attachments.filter(a => !a.deleted);
  const hasMaxAttachments = activeAttachments.length >= MAX_ATTACHMENTS;
  const isFreePage = activeFacebookPage?.free;
  const showSentFromMMWarning =
    (!!activeFacebookPage?.redemption?.freeTrial &&
      !(activeFacebookPage?.subscription?.state === 'future')) ||
    isFreePage;

  const showGeneratePrompt =
    !cache?.current?.body &&
    userWritesManually === null &&
    !editor.state.doc.textContent &&
    !activeAttachments.length;

  const onRemoveAttachment = (attachmentUniqId: string) => {
    setAttachments(as =>
      as.map(a =>
        a.uniqId === attachmentUniqId ? { ...a, deleted: true } : a,
      ),
    );
  };

  const {
    getRootProps,
    getInputProps,
    open: openAttachmentPrompt,
  } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    accept: ACCEPTED_FILE_TYPES,
    onDrop: (files: File[], rejectedFiles: File[]) => {
      if (files.length) {
        if (activeAttachments.length + files.length > MAX_ATTACHMENTS) {
          modalActions.openGeneralTextModal({
            header: 'Cannot add attachments',
            message: `Maximum of ${MAX_ATTACHMENTS} attachments per email message`,
          });
          return;
        }
        for (const file of files) {
          const uniqId = getUniqId('email-attachment');
          const attachment = {
            uniqId,
            name: file.name,
            added: true,
            uploadPromise: uploadActions.uploadEmailAttachment(
              {
                content: file,
                extension: getFormatFromType(file.type),
              },
              true,
            ),
          };
          setAttachments(as => [...as, attachment]);
          attachment.uploadPromise
            .then(id => {
              setAttachments(as =>
                as.map(a =>
                  a.uniqId === uniqId
                    ? { ...a, id, uploadPromise: undefined }
                    : a,
                ),
              );
              return id;
            })
            .catch(err => {
              setAttachments(as =>
                as.map(a =>
                  a.uniqId === uniqId
                    ? {
                        ...a,
                        uploadPromise: undefined,
                        uploadError: err.toString(),
                      }
                    : a,
                ),
              );
            });
        }
      } else if (rejectedFiles.length) {
        if (rejectedFiles[0].size > MAX_FILE_SIZE) {
          modalActions.openGeneralTextModal({
            header: 'Cannot add attachment',
            message: `Attachment size is too big - maximum file size is ${MAX_FILE_SIZE_IN_MB}MB`,
          });
        } else {
          modalActions.openGeneralTextModal({
            header: 'Cannot add attachment',
            message: 'Unsupported file type',
          });
        }
      }
    },
  });

  const extractSubject = (text: string) => {
    const indexOfSubjectEnd = text.indexOf('\n\n') + 1;
    return text?.slice(0, indexOfSubjectEnd)?.replace('Subject:', '')?.trim();
  };

  const handleGeneratedEmailPaste = (text: string) => {
    const generatedSubject = extractSubject(text);
    if (generatedSubject) {
      updateConfig('subject', generatedSubject);
      setSubjectValue(generatedSubject);
    }

    editor.insertParagraphs(text);
  };

  const onGenerateButtonClick = () =>
    viewActions.showSlideoutMenu('automationEmailGenerate', {
      onSubmit: handleGeneratedEmailPaste,
      getCurrentEmailString: () => editor.getHTMLStringFromState(editor.state),
    });

  const filterDuplicates = (toFilter: string[], refArr: string[]) =>
    toFilter.filter(v => !refArr.includes(v));

  const processEmails = (inputString: string, state: string[], cap: number) => {
    const extracted = extractEmailsFromString(inputString);
    const uniq = filterDuplicates(extracted, state);

    return [...state, ...uniq].slice(0, cap);
  };

  const handleCCSubmit = () => {
    const emails = processEmails(ccInputValue, ccEmails, CC_CAP);

    setCCEmails(emails);
    setCcInputValue('');
    updateConfig('cc', emails.join(','));
  };

  const handleCCDelete = (emailToDelete: string) => {
    const updatedEmails = ccEmails.filter(v => v !== emailToDelete);
    setCCEmails(updatedEmails);
    updateConfig('cc', updatedEmails.join(','));
  };

  const handleBCCSubmit = () => {
    const emails = processEmails(bccInputValue, bccEmails, BCC_CAP);

    setBCCEmails(emails);
    setBccInputValue('');
    updateConfig('bcc', emails.join(','));
  };

  const handleBCCDelete = (emailToDelete: string) => {
    const updatedEmails = bccEmails.filter(v => v !== emailToDelete);
    setBCCEmails(updatedEmails);
    updateConfig('bcc', updatedEmails.join(','));
  };

  useEffect(() => {
    const html = !editor.state.doc.textContent
      ? ''
      : editor.getHTMLStringFromState(editor.state);

    if (!isEqual(bodyValue, html)) {
      updateConfig('body', html);
      setBodyValue(html);
    }

    if (!isEqual(subjectValue, currentEntity?.subject)) {
      updateConfig('subject', subjectValue);
    }
    if (!isEqual(previewTextValue, currentEntity?.previewText)) {
      updateConfig('previewText', previewTextValue);
    }

    if (!isEqual(attachments, currentEntity?.emailAttachments)) {
      updateConfig('emailAttachments', attachments);
    }
  }, [editor.state, subjectValue, previewTextValue, attachments]);

  return (
    <div className={classNames(css.form, styles?.container)}>
      <div className={css.field}>
        <span className={css.fieldName}>Email subject</span>
        <AttributeContainingTextInput
          className={css.input}
          value={subjectValue}
          onChange={onChangeSubject}
          inputRef={setEmailSubjectRef}
          useStoreAttributes
        >
          {ai.processing && (
            <div className={cssForm.aiProcessingSubjectContainer}>
              <Spinner standalone size={14} width={14} />
              <span>Generating email subject... May take up to 1 minute</span>
            </div>
          )}
          <div className={css.inputActions}>
            <EmojiPicker
              className={css.emoji}
              onEmojiPicked={updateInsertEmojiSubject}
            >
              <SmileOutlined />
            </EmojiPicker>

            <AttributePicker
              className={css.picker}
              onAttributePicked={updateInsertAttributeSubject}
            >
              <BracketsOutlined className={css.code} />
            </AttributePicker>
          </div>
        </AttributeContainingTextInput>
      </div>

      <div className={css.field}>
        <span className={css.fieldName}>
          Email preview
          <Tooltip
            title="The lighter text that is shown after the subject in your recipients'
            inboxes"
          >
            <QuestionCircleOutlined className={cssForm.infoIcon} />
          </Tooltip>
        </span>
        <AttributeContainingTextInput
          className={css.input}
          value={previewTextValue}
          onChange={onChangePreviewText}
          inputRef={setEmailPreviewTextRef}
          useStoreAttributes
        >
          <div className={css.inputActions}>
            <EmojiPicker
              className={css.emoji}
              onEmojiPicked={updateInsertEmojiPreviewText}
            >
              <SmileOutlined />
            </EmojiPicker>

            <AttributePicker
              className={css.picker}
              onAttributePicked={updateInsertAttributePreviewText}
            >
              <BracketsOutlined className={css.code} />
            </AttributePicker>
          </div>
        </AttributeContainingTextInput>
      </div>

      <div className={css.field}>
        <span className={css.fieldName}>Email body</span>

        <div>
          <span className={css.fieldName}>
            <Dropdown
              menu={editorDropdownMenuProps}
              autoAdjustOverflow
              overlayStyle={{ zIndex: 3500002 }}
              trigger={['click']}
              disabled={!!emailSendsCount} // Note: we don't allow changing the editor type after the email has been sent not to mess with stats
            >
              <Button
                color="secondary"
                css={{
                  container: cssForm.ddEditorDropdownButton,
                }}
                rightIcon={<DownOutlined />}
              >
                <Space>
                  {editorDropdownItems.find(e => e.key === selectedEditorType)
                    ?.label || ''}
                </Space>
              </Button>
            </Dropdown>
            <Tooltip
              title="After you start sending emails, you cannot change email body type
              not to ruin statistics"
            >
              <ExclamationCircleOutlined className={cssForm.infoIcon} />
            </Tooltip>
          </span>
        </div>

        {selectedEditorType === 1 ? (
          <div>
            <HtmlEditor stateAndHandlers={stateAndHandlers} />
          </div>
        ) : (
          <div>
            <div className={css.toolbar}>
              <BoldOutlined
                className={classNames(css.editorButton, {
                  [css.active]: editor.isBold(editor.state),
                })}
                onClick={() =>
                  editor.toggleBold(editor.state, tr =>
                    editor.setState(editor.state.apply(tr)),
                  )
                }
              />

              <ItalicOutlined
                className={classNames(css.editorButton, {
                  [css.active]: editor.isItalic(editor.state),
                })}
                onClick={() =>
                  editor.toggleItalic(editor.state, tr =>
                    editor.setState(editor.state.apply(tr)),
                  )
                }
              />

              <UnderlineOutlined
                className={classNames(css.editorButton, {
                  [css.active]: editor.isUnderline(editor.state),
                })}
                onClick={() =>
                  editor.toggleUnderline(editor.state, tr =>
                    editor.setState(editor.state.apply(tr)),
                  )
                }
              />

              <StrikethroughOutlined
                className={classNames(css.editorButton, {
                  [css.active]: editor.isStrikethrough(editor.state),
                })}
                onClick={() =>
                  editor.toggleStrikethrough(editor.state, tr =>
                    editor.setState(editor.state.apply(tr)),
                  )
                }
              />

              <span className={css.separator} />
              <UnorderedListOutlined
                className={classNames(css.editorButton, {
                  [css.active]: editor.isUnorderedList(editor.state),
                })}
                onClick={() =>
                  editor.toggleUnorderedList(editor.state, tr =>
                    editor.setState(editor.state.apply(tr)),
                  )
                }
              />

              <OrderedListOutlined
                className={classNames(css.editorButton, {
                  [css.active]: editor.isOrderedList(editor.state),
                })}
                onClick={() =>
                  editor.toggleOrderedList(editor.state, tr =>
                    editor.setState(editor.state.apply(tr)),
                  )
                }
              />
              <span className={css.separator} />

              <LinkOutlined
                className={classNames(css.editorButton, {
                  [css.active]: editor.isLink(editor.state),
                })}
                onClick={() =>
                  editor.toggleLink(editor.state, tr =>
                    editor.setState(editor.state.apply(tr)),
                  )
                }
              />

              <FileImageOutlined
                className={classNames(css.editorButton, {
                  [css.editorButtonDisabled]: !editor.canInsertImageAtCursor(
                    editor.state,
                  ),
                })}
                onClick={() => {
                  if (editor.canInsertImageAtCursor(editor.state)) {
                    imageUploadSelectorRef.current?.click();
                  }
                }}
              />

              <EmojiPicker
                className={classNames(css.emoji, css.editorButton)}
                onEmojiPicked={emoji => {
                  editor.insertText(emoji);
                  editorRef.current?.view?.focus?.();
                }}
              >
                <SmileOutlined />
              </EmojiPicker>

              <AttributePicker
                className={css.picker}
                onAttributePicked={insertBodyAttribute}
              >
                <BracketsOutlined className={css.code} />
              </AttributePicker>

              <span className={css.separator} />
              <PaperClipOutlined
                className={classNames(css.editorButton, {
                  [css.editorButtonDisabled]: hasMaxAttachments,
                })}
                title={
                  hasMaxAttachments
                    ? `Maximum of ${MAX_ATTACHMENTS} attachments per email message`
                    : ''
                }
                onClick={() => {
                  if (!hasMaxAttachments) {
                    openAttachmentPrompt();
                  }
                }}
              />
            </div>

            <div className={css.dropzone} {...getRootProps()}>
              <input {...getInputProps()} />
            </div>
            <div className={css.imageUpload}>
              <input
                type="file"
                ref={imageUploadSelectorRef}
                onChange={onImageSelected}
                accept="image/*"
                multiple
              />
            </div>

            <div className={css.editorContainer}>
              {ai.processing && (
                <div
                  className={cssForm.aiProcessingBodyContainer}
                  onClick={onAiContainerClick}
                >
                  <div className={cssForm.aiProcessingBodySpinnerContainer}>
                    <Spinner standalone size={14} width={14} />
                    <span className={cssForm.aiProcessingBodySpinnertext}>
                      Generating email body...
                      <br />
                      May take up to 1 minute
                    </span>
                  </div>
                  <div className={cssForm.aiProcessingBodyTooltipContainer}>
                    <span>
                      Click to cancel AI generated email and type manually
                    </span>
                  </div>
                </div>
              )}
              {showGeneratePrompt && !ai.processing && (
                <div className={css.generatePrompt}>
                  <Button
                    css={{ container: css.generatePromptButton }}
                    leftIcon={<MagicPencilIcon />}
                    onClick={() => {
                      setUserWritesManually(false);
                      onGenerateButtonClick();
                    }}
                  >
                    Generate Email automatically
                  </Button>
                </div>
              )}

              <ProseMirror
                ref={editorRef}
                className={css.editor}
                state={editor.state}
                onChange={editor.setState}
                // @ts-ignore
                handleDrop={handleDrop}
                handlePaste={handlePaste}
                handleClick={() => {
                  setUserWritesManually(true);
                  editorRef.current?.view?.focus?.();
                }}
              />
              {!!activeAttachments.length && (
                <div className={css.attachments}>
                  {activeAttachments.map((attachment, i) => (
                    <div key={attachment.uniqId} className={css.attachment}>
                      <div>{attachment.name}</div>
                      <div className={css.attachmentStatus}>
                        {!!attachment.uploadPromise && (
                          <Spinner standalone size={8} />
                        )}
                        {!!attachment.uploadError && (
                          <ErrorIcon color={colors.red} />
                        )}
                        <CloseOutlined
                          onClick={() => onRemoveAttachment(attachment.uniqId)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {editor.link && (
                <LinkPrompt
                  link={editor.link}
                  onSubmit={link => {
                    editor.updateLink(
                      editor.state,
                      tr => editor.setState(editor.state.apply(tr)),
                      link,
                    );
                  }}
                  onCancel={editor.closeLink}
                />
              )}

              {!showGeneratePrompt && !ai.processing && (
                <Button
                  css={{ container: css.generateButton }}
                  leftIcon={<MagicPencilIcon />}
                  onClick={onGenerateButtonClick}
                >
                  Generate automatically
                </Button>
              )}
            </div>

            {showSentFromMMWarning && (
              <div className={css.freeTrialFooter}>
                Emails sent from {isFreePage ? 'Free' : 'Free Trial'} accounts
                have
                <br />
                "Sent from my Customers.ai" branding.{' '}
                <Button color="link" onClick={onUpgradeClick}>
                  Upgrade now to remove
                </Button>
              </div>
            )}
            {imagesScaledDown && (
              <Alert
                type="warning"
                showCloseIcon={false}
                className={css.bottomAlert}
              >
                Large images are automatically scaled down to a maximum width of{' '}
                {maxImageWidth}px and a maximum height of {maxImageHeight}px.
              </Alert>
            )}
          </div>
        )}
      </div>

      <div className={css.signatureLink}>
        <div className={css.link} onClick={onSignatureClick}>
          Edit email signature
        </div>
        <QuestionCircleOutlined
          className={css.questionMark}
          onClick={() =>
            window.open(
              'https://customers.ai/help/l/en/article/870e9puvfd-edit-email-signature',
            )
          }
        />
      </div>

      <div
        className={classNames(
          css.boxWrapper,
          css.optionalBlock,
          styles?.optionalBlock,
        )}
      >
        <div className={css.field}>
          <span className={css.fieldName}>Optional email(s) to CC:</span>

          <Input
            value={ccDisabled ? ccMaxReachText : ccInputValue}
            placeholder={emailInputPlaceholder}
            disabled={ccDisabled}
            css={{ container: classNames(css.input, css.ccInput) }}
            onChange={e => setCcInputValue(e.target.value || '')}
            onSearch={handleCCSubmit}
          />

          {!!ccEmails.length && (
            <div className={css.emailList}>
              {ccEmails.map(email => (
                <Chip type="info" text={email} key={email}>
                  <CloseOutlined
                    className={css.emailDeleteIcon}
                    onClick={() => handleCCDelete(email)}
                  />
                </Chip>
              ))}
            </div>
          )}

          <span className={css.fieldHelper}>
            Up to {CC_CAP} emails separated with a comma
          </span>
        </div>

        <div className={css.field}>
          <span className={css.fieldName}>Optional email(s) to BCC:</span>

          <Input
            value={bccDisabled ? bccMaxReachText : bccInputValue}
            placeholder={emailInputPlaceholder}
            disabled={bccDisabled}
            css={{ container: classNames(css.input, css.ccInput) }}
            onChange={e => setBccInputValue(e.target.value || '')}
            onSearch={handleBCCSubmit}
          />

          {!!bccEmails.length && (
            <div className={css.emailList}>
              {bccEmails.map(email => (
                <Chip type="info" text={email} key={email}>
                  <CloseOutlined
                    className={css.emailDeleteIcon}
                    onClick={() => handleBCCDelete(email)}
                  />
                </Chip>
              ))}
            </div>
          )}

          <span className={css.fieldHelper}>
            Up to {CC_CAP} emails separated with a comma
          </span>
        </div>
      </div>
    </div>
  );
};
